import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { deleteAccount, getUserProfile } from '../../services/userService';

const AccountSettings: React.FC = () => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();

    // Profile data state
    const [profileData, setProfileData] = useState<{
        userId: string;
        emailId: string;
        name: string;
        profileUrl: string;
        companyName: string;
        phoneNumber: string;
        registrationDate: string;
        credits: number;
        accountType?: string;
        lastSignIn?: string;
    } | null>(null);

    // UI states
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(true);

    // Delete account confirmation states
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

    const showMessage = (message: string, isError: boolean = false) => {
        if (isError) {
            setError(message);
            setSuccess('');
        } else {
            setSuccess(message);
            setError('');
        }
        setTimeout(() => {
            setError('');
            setSuccess('');
        }, 4000);
    };

    // Fetch profile data from API
    useEffect(() => {
        const fetchProfileData = async () => {
            if (!currentUser?.uid) {
                setLoadingProfile(false);
                return;
            }

            try {
                setLoadingProfile(true);
                const response = await getUserProfile(currentUser.uid);

                if (response.success) {
                    setProfileData(response.data);
                } else {
                    showMessage('Failed to load profile data', true);
                }
            } catch (error) {
                console.error('Error fetching profile data:', error);
                showMessage('Failed to load profile data', true);
            } finally {
                setLoadingProfile(false);
            }
        };

        fetchProfileData();
    }, [currentUser?.uid]);

    // Handler functions for delete account functionality only

    const handleDeleteAccount = async () => {
        setShowDeleteConfirmation(true);
    };

    const confirmDeleteAccount = async () => {
        if (!currentUser?.uid) {
            showMessage('User not authenticated', true);
            return;
        }

        if (deleteConfirmationText.trim() !== 'DELETE') {
            showMessage('Please type "DELETE" to confirm account deletion', true);
            return;
        }

        try {
            setLoading(true);
            setError('');

            const response = await deleteAccount(currentUser.uid);

            if (response.success) {
                showMessage('Account deleted successfully. You will be logged out shortly.');

                // Wait a bit to show the success message, then logout and redirect
                setTimeout(async () => {
                    try {
                        await logout();
                        navigate('/login');
                    } catch (logoutError) {
                        console.error('Error during logout:', logoutError);
                        // Force redirect even if logout fails
                        window.location.href = '/login';
                    }
                }, 2000);
            } else {
                showMessage('Failed to delete account. Please try again.', true);
            }
        } catch (err) {
            console.error('Account deletion failed:', err);
            showMessage('Failed to delete account. Please try again.', true);
        } finally {
            setLoading(false);
            setShowDeleteConfirmation(false);
            setDeleteConfirmationText('');
        }
    };

    const cancelDeleteAccount = () => {
        setShowDeleteConfirmation(false);
        setDeleteConfirmationText('');
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Account Settings</h1>

            {/* Status Messages */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    {error}
                </div>
            )}
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                    <i className="fas fa-check-circle mr-2"></i>
                    {success}
                </div>
            )}

            {/* Account Information */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <i className="fas fa-info-circle mr-2 text-blue-600"></i>
                    Account Information
                </h2>

                {loadingProfile ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-text-description">Loading profile data...</span>
                    </div>
                ) : profileData ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-medium text-text-heading">Full Name:</span>
                            <span className="ml-2 text-text-description">{profileData.name}</span>
                        </div>
                        <div>
                            <span className="font-medium text-text-heading">Email:</span>
                            <span className="ml-2 text-text-description">{profileData.emailId}</span>
                        </div>
                        <div>
                            <span className="font-medium text-text-heading">Account Type:</span>
                            <span className="ml-2 text-text-description">{profileData.accountType || 'Standard User'}</span>
                        </div>
                        <div>
                            <span className="font-medium text-text-heading">Credits:</span>
                            <span className="ml-2 text-yellow-600 font-medium">{profileData.credits}</span>
                        </div>
                        <div>
                            <span className="font-medium text-text-heading">Company:</span>
                            <span className="ml-2 text-text-description">
                                {profileData.companyName || 'Not specified'}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium text-text-heading">Phone:</span>
                            <span className="ml-2 text-text-description">
                                {profileData.phoneNumber || 'Not specified'}
                            </span>
                        </div>
                        <div>
                            <span className="font-medium text-text-heading">Member Since:</span>
                            <span className="ml-2 text-text-description">
                                {profileData.registrationDate
                                    ? new Date(profileData.registrationDate).toLocaleDateString()
                                    : 'Unknown'
                                }
                            </span>
                        </div>
                        <div>
                            <span className="font-medium text-text-heading">Last Sign In:</span>
                            <span className="ml-2 text-text-description">
                                {profileData.lastSignIn
                                    ? new Date(profileData.lastSignIn).toLocaleDateString()
                                    : (currentUser?.metadata?.lastSignInTime
                                        ? new Date(currentUser.metadata.lastSignInTime).toLocaleDateString()
                                        : 'Unknown')
                                }
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8 text-text-description">
                        <i className="fas fa-exclamation-triangle text-2xl mb-2"></i>
                        <p>Failed to load profile data</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-2 text-blue-600 hover:text-blue-800"
                        >
                            Try again
                        </button>
                    </div>
                )}
            </div>

            {/* Danger Zone */}
            <div className="mt-6 bg-white p-6 rounded-lg shadow border border-red-200">
                <h2 className="text-xl font-semibold mb-4 text-red-600 flex items-center">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Danger Zone
                </h2>
                <div className="bg-red-50 p-4 rounded-md">
                    <h3 className="text-lg font-medium text-red-800 mb-2">Delete Account</h3>
                    <p className="text-red-700 text-sm mb-4">
                        Once you delete your account, there is no going back. This will permanently delete your account and remove your data from our servers.
                    </p>
                    <button
                        onClick={handleDeleteAccount}
                        disabled={loading}
                        className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                        {loading ? 'Processing...' : 'Delete Account'}
                    </button>
                </div>
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 relative">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-red-600">⚠️ Delete Account</h2>
                            <button
                                onClick={cancelDeleteAccount}
                                className="text-text-description hover:text-text-description"
                                disabled={loading}
                            >
                                <i className="fas fa-times text-lg"></i>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="mb-6">
                            <div className="bg-red-50 border border-red-200 p-4 rounded-md mb-4">
                                <p className="text-red-800 text-sm font-medium mb-2">
                                    ⚠️ This action cannot be undone!
                                </p>
                                <p className="text-red-700 text-sm">
                                    This will permanently delete:
                                </p>
                                <ul className="text-red-700 text-sm mt-2 ml-4 list-disc">
                                    <li>Your account and profile</li>
                                    <li>All your generated images</li>
                                    <li>Your credit history</li>
                                    <li>All personal data</li>
                                </ul>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-heading mb-2">
                                    Type <span className="font-bold text-red-600">"DELETE"</span> to confirm:
                                </label>
                                <input
                                    type="text"
                                    value={deleteConfirmationText}
                                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                                    className="w-full px-3 py-2 border border-accent-taupe rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Type DELETE here"
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex space-x-3">
                            <button
                                onClick={cancelDeleteAccount}
                                disabled={loading}
                                className="flex-1 bg-accent-taupe text-text-heading py-2 px-4 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteAccount}
                                disabled={loading || deleteConfirmationText.trim().toUpperCase() !== 'DELETE'}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Deleting...
                                    </div>
                                ) : (
                                    'Delete Forever'
                                )}
                            </button>
                        </div>

                        {/* Warning text */}
                        <p className="text-xs text-text-description text-center mt-4">
                            This action is irreversible. All your data will be permanently lost.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountSettings;